import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../utils/apiClient';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { SERVER_BASE } from '../config/server';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faEllipsisV, faThumbsUp, faTrash } from '@fortawesome/free-solid-svg-icons';
import { hasPermission } from "../utils/usePermissions";
import { useTable, usePagination, useSortBy } from 'react-table';


// -----------------------------------------
//               LedgerTable
// -----------------------------------------
const LedgerTable = ({ data, onEdit, onDelete, onApprove, role, hasPermission, pageSize, setPageSize, tabKey }) => {
  const savedPageIndex = parseInt(localStorage.getItem(`ledgerPage-${tabKey}`)) || 0;

  const columns = useMemo(
    () => [
      { Header: 'Group', accessor: 'ledgerGroup' },
      { Header: 'Name', accessor: 'ledgerName' },
      { Header: 'Address', accessor: 'address' },
      { Header: 'City', accessor: 'city' },
      {
        Header: 'State',
        accessor: row => `${row.state ? `${row.state} (${row.stateCode})` : ''}`,
        id: 'state',
      },
      {
        Header: 'GSTIN',
        accessor: row => row.gstin || '',
        id: 'gstin',
      },
      { Header: 'Center', accessor: 'centerType' },
      { Header: 'Area Jurisd.', accessor: 'areaJurisdiction' },
      { Header: 'Print Name', accessor: 'printName' },
      {
        Header: 'Actions',
        Cell: ({ row }) => (
          <div className="dropdown">
            <div className="d-flex gap-2 align-items-center">
              {(role === 'itManager' || hasPermission('ledgerMaster', 3)) && (
                <button className="btn btn-sm btn-outline-info" onClick={() => onEdit(row.original)}>
                  <FontAwesomeIcon icon={faEdit} />
                </button>
              )}

              <button className="btn btn-sm btn-light" type="button" data-bs-toggle="dropdown">
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>

              <ul className="dropdown-menu">
                {(role === 'itManager' || hasPermission('ledgerMaster', 4)) && (
                  <li>
                    <button className="dropdown-item text-danger" onClick={() => onDelete(row.index, row.original)}>
                      <FontAwesomeIcon icon={faTrash} className="me-2" />
                      Delete
                    </button>
                  </li>
                )}

                {(role === 'itManager' && row.original.status == 'Draft') && (
                  <li>
                    <button className="dropdown-item" onClick={() => onApprove(row.original)}>
                      <FontAwesomeIcon icon={faThumbsUp} className="me-2" />
                      Approve
                    </button>
                  </li>
                )}
              </ul>
            </div>
          </div>
        ),
      }
    ],
    [data, role, hasPermission]
  );


  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    state: { pageIndex }
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: savedPageIndex, pageSize: pageSize },
    },
    useSortBy,
    usePagination
  );

  useEffect(() => {
    localStorage.setItem(`ledgerPage-${tabKey}`, pageIndex);
  }, [pageIndex, tabKey]);

  useEffect(() => {
    localStorage.setItem(`ledgerPageSize-${tabKey}`, pageSize);
  }, [pageSize, tabKey]);

  return (
    <>
      <div className="table-responsive">
        <table {...getTableProps()} className="table table-striped table-sm" style={{ fontSize: '14px' }}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>{column.render('Header')}</th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map(row => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-2">
        <button className="btn btn-secondary" onClick={previousPage} disabled={!canPreviousPage}>
          Previous
        </button>

        <span>
          Page {pageIndex + 1} of {pageOptions.length}
        </span>

        <button className="btn btn-secondary" onClick={nextPage} disabled={!canNextPage}>
          Next
        </button>
      </div>
    </>
  );
};



// -----------------------------------------
//          ApproveLedgerMaster Page
// -----------------------------------------
const ApproveLedgerMaster = () => {
  const [ledgers, setLedgers] = useState([]);
  const [filteredLedgers, setFilteredLedgers] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState({ value: 'Draft', label: 'Draft' });
  const [groupFilter, setGroupFilter] = useState(null);
  const [stateFilter, setStateFilter] = useState(null);

  const [groupOptions, setGroupOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);

  const [pageSize, setPageSize] = useState(
    parseInt(localStorage.getItem(`ledgerPageSize-${statusFilter.value}`)) || 25
  );

  const code = localStorage.getItem("code");
  const email = localStorage.getItem("email");
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  const navigate = useNavigate();


  useEffect(() => {
    fetchLedgerMasters();
  }, []);


  const fetchLedgerMasters = async () => {
    try {
      const response = await apiClient.get(SERVER_BASE + '/ledgerMastersList', {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data || [];
      setLedgers(data);

      // Group filter options
      const groups = [...new Set(data.map(l => l.ledgerGroup).filter(Boolean))].map(g => ({
        value: g,
        label: g
      }));
      setGroupOptions(groups);

      // State filter
      const states = [...new Set(data.map(l => l.state).filter(Boolean))].map(s => ({
        value: s,
        label: s
      }));
      setStateOptions(states);

    } catch (error) {
      console.error('Error:', error);
    }
  };


  // Filtering logic
  useEffect(() => {
    let filtered = [...ledgers];

    if (statusFilter) filtered = filtered.filter(l => l.status === statusFilter.value);
    if (groupFilter) filtered = filtered.filter(l => l.ledgerGroup === groupFilter.value);
    if (stateFilter) filtered = filtered.filter(l => l.state === stateFilter.value);

    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      filtered = filtered.filter(l =>
        Object.values(l).join(" ").toLowerCase().includes(t)
      );
    }

    setFilteredLedgers(filtered);

  }, [ledgers, statusFilter, groupFilter, stateFilter, searchTerm]);



  // Handlers stay unchanged
  const handleEdit = row => navigate(`../LedgerMasterForm`, { state: { row } });

  const handleApprove = async row => {
    const { id } = row;

    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "Approve this Ledger?",
      showCancelButton: true,
      icon: "warning",
    });

    if (confirm.isConfirmed) {
      try {
        await apiClient.put(`${SERVER_BASE}/approveLedgerMaster`, { id, code, email }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        Swal.fire("Approved!", "", "success");
        fetchLedgerMasters();

      } catch (e) {
        Swal.fire("Error", "Failed to approve", "error");
      }
    }
  };

  const handleDelete = async (index, data) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: 'This cannot be undone!',
      showCancelButton: true,
      icon: 'warning',
    });

    if (confirm.isConfirmed) {
      try {
        await apiClient.delete(`${SERVER_BASE}/deleteLedgerMaster/${data.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        Swal.fire({
          text: 'Deleted',
          icon: 'success',
          showConfirmButton:false,
          timer:2000,
          timerProgressBar:true,
          toast:true,
          position:'top-end'
        });
        fetchLedgerMasters();

      } catch (err) {
        Swal.fire('Error', 'Could not delete', 'error');
      }
    }
  };



  const statusOptions = [
    { value: 'Draft', label: 'Draft' },
    { value: 'Final', label: 'Final' }
  ];



  // -----------------------------------------
  //                  UI
  // -----------------------------------------
  return (
    <div className="formcontainer mt-5">
      <h3>Approve Ledgers</h3>

      {/* Filters */}
      <div className="card p-3 mb-3 shadow-sm">
        <div className="row g-3 align-items-end">

          {/* Search */}
          <div className="col-md-4">
            <input
              type="text"
              className="form-control"
              placeholder="Search ledgers..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Status */}
          <div className="col-md-2">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setPageSize(25); // reset size on status change
              }}
              placeholder="Status"
            />
          </div>

          {/* Group */}
          <div className="col-md-3">
            <Select
              options={groupOptions}
              value={groupFilter}
              onChange={setGroupFilter}
              placeholder="Group"
              isClearable
            />
          </div>

          {/* State */}
          <div className="col-md-3">
            <Select
              options={stateOptions}
              value={stateFilter}
              onChange={setStateFilter}
              placeholder="State"
              isClearable
            />
          </div>

          {/* Page Size */}
          <div className="col-md-2">
            <select
              className="form-select"
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
            >
              {[5, 10, 25, 50, 100].map(s => (
                <option key={s} value={s}>{s} / page</option>
              ))}
            </select>
          </div>

          {/* Record Count */}
          <div className="col-md-3">
            <input
              type="text"
              className="form-control text-center fw-semibold"
              value={`Showing ${filteredLedgers.length} of ${ledgers.length} records`}
              readOnly
            />
          </div>

        </div>
      </div>

      {/* Table */}
      <LedgerTable
        data={filteredLedgers}
        onEdit={handleEdit}
        onApprove={handleApprove}
        onDelete={handleDelete}
        role={role}
        hasPermission={hasPermission}
        pageSize={pageSize}
        setPageSize={setPageSize}
        tabKey={statusFilter?.value || "Draft"}
      />

    </div>
  );
};

export default ApproveLedgerMaster;
